import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { DialogTrigger, Dialog as AriaDialog } from 'react-aria-components';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import { StarRating, Button, TextArea, Popover } from '@/interfaces/session-player/ui';
import {
  useSubmitSessionRating,
  useSubmitSessionFeedback,
} from '@/shared/api/hooks/useStudySessions';

interface SessionFeedbackProps {
  sessionId: string;
  /** Rating value from parent state (survives remounts). Null = not yet rated. */
  submittedRating: number | null;
  /** Whether feedback text has been submitted. */
  feedbackSent: boolean;
  /** Called immediately when user clicks a star (optimistic, survives remount). */
  onRated: (rating: number) => void;
  /** Called if the rating API call fails, so parent can reset. */
  onRatingFailed: () => void;
  /** Called when feedback text is successfully submitted. */
  onFeedbackSent: () => void;
}

export function SessionFeedback({
  sessionId,
  submittedRating,
  feedbackSent,
  onRated,
  onRatingFailed,
  onFeedbackSent,
}: SessionFeedbackProps) {
  const [feedbackText, setFeedbackText] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);

  const ratingMutation = useSubmitSessionRating();
  const feedbackMutation = useSubmitSessionFeedback();

  const hasRating = submittedRating != null;

  const handleRate = (value: number) => {
    if (hasRating) return;
    onRated(value); // Optimistic — set in parent immediately so it survives remounts
    ratingMutation.mutate(
      { sessionId, rating: value },
      {
        onError: () => {
          onRatingFailed();
        },
      }
    );
  };

  const handleSubmitFeedback = () => {
    const trimmed = feedbackText.trim();
    if (!trimmed) return;
    feedbackMutation.mutate(
      { sessionId, feedback: trimmed },
      {
        onSuccess: () => {
          setPopoverOpen(false);
          onFeedbackSent();
        },
        onError: () => {
          toast.error('Could not send comment. Please try again.');
        },
      }
    );
  };

  return (
    <motion.div
      className="flex flex-col items-center gap-2"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.95, duration: 0.35 }}
    >
      <StarRating value={submittedRating ?? undefined} onRate={handleRate} disabled={hasRating} />

      <AnimatePresence>
        {hasRating && !feedbackSent && (
          <motion.div
            key="add-comment"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            <DialogTrigger isOpen={popoverOpen} onOpenChange={setPopoverOpen}>
              <Button
                variant="link"
                className="mt-1 text-sm font-medium"
                style={{ color: 'var(--f-fg-muted)' }}
              >
                Add a comment
              </Button>
              <Popover placement="top" className="w-72 p-3">
                <AriaDialog className="outline-none">
                  <div className="flex flex-col gap-2">
                    <TextArea
                      aria-label="Session feedback"
                      placeholder="What did you think?"
                      rows={3}
                      value={feedbackText}
                      onChange={(v) => setFeedbackText(v)}
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      onPress={handleSubmitFeedback}
                      isDisabled={!feedbackText.trim() || feedbackMutation.isPending}
                      className="self-end"
                    >
                      <Send size={14} />
                      Send
                    </Button>
                  </div>
                </AriaDialog>
              </Popover>
            </DialogTrigger>
          </motion.div>
        )}

        {feedbackSent && (
          <motion.p
            key="comment-sent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-1 text-sm"
            style={{ color: 'var(--f-fg-muted)' }}
          >
            Comment sent
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
